import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { axiosInterceptor } from "app/services/http-services/request-interceptor";

interface TranslationResolverOptions {
    all?: boolean;
}

interface ResolveTranslationsResponse {
    success: boolean;
    data: Record<string, string>;
    meta: Record<string, unknown>;
    error: unknown;
}

export const TRANSLATION_RESOLVE_QUERY_KEY_PREFIX = "translation-resolve";
const pendingResolutions = new Map<string, Promise<Pick<ResolveTranslationsResponse, "data" | "meta">>>();

export const translationResolveQueryKey = (language: string, keys: string[]) => [
    TRANSLATION_RESOLVE_QUERY_KEY_PREFIX,
    language,
    [...keys].sort().join(","),
];

export const fetchTranslationResolve = (keys: string[], language: string, all = false): Promise<Pick<ResolveTranslationsResponse, "data" | "meta">> => {
    const normalizedKeys = [...new Set(keys.map((key) => String(key || "").trim()).filter(Boolean))].sort();
    const requestKey = JSON.stringify([language, all, normalizedKeys]);
    const pending = pendingResolutions.get(requestKey);
    if (pending) return pending;

    const request = axiosInterceptor.post<ResolveTranslationsResponse>("/api/translations/resolve", {
        keys: normalizedKeys,
        language,
        all,
    }).then((response) => ({ data: response.data.data || {}, meta: response.data.meta || {} }))
        .finally(() => { pendingResolutions.delete(requestKey); });
    pendingResolutions.set(requestKey, request);
    return request;
};

export const useTranslationResolver = (keys: string[], language: string, enabled = true, options: TranslationResolverOptions = {}) => {
    const normalizedKeys = useMemo(
        () => [...new Set(keys.map((key) => String(key || "").trim()).filter(Boolean))].sort(),
        [keys]
    );

    return useQuery({
        queryKey: translationResolveQueryKey(language, normalizedKeys),
        queryFn: async () => (await fetchTranslationResolve(normalizedKeys, language, options.all ?? false)).data,
        enabled: enabled && Boolean(language) && (options.all === true || normalizedKeys.length > 0),
        staleTime: 1000 * 60 * 60,
        retry: 1,
    });
};
